import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ChevronRight, ShieldCheck, Gauge, Zap, Terminal } from 'lucide-react';
import { cn } from '@/lib/utils';
export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  return (
    <div className="relative min-h-screen bg-hpmDarker flex flex-col items-center justify-center overflow-hidden font-sans">
      {/* Advanced Carbon Fiber Pattern Overlay */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(45deg, #ffffff 12.5%, transparent 12.5%, transparent 87.5%, #ffffff 87.5%),
            linear-gradient(135deg, #ffffff 12.5%, transparent 12.5%, transparent 87.5%, #ffffff 87.5%)
          `,
          backgroundSize: '16px 16px',
          backgroundPosition: '0 0, 8px 8px'
        }}
      />
      {/* Multi-Layer Neon Glow Centerpiece */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-hpmBlue/5 blur-[180px] rounded-full pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-hpmBlue/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full flex flex-col items-center">
        {/* HPM Branding Hero Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col items-center mb-24 text-center"
        >
          <div className="relative group mb-10">
            {/* Pulsing Backglow */}
            <motion.div 
              animate={{ opacity: [0.4, 0.8, 0.4], scale: [0.95, 1.05, 0.95] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 bg-hpmBlue/30 blur-3xl rounded-full" 
            />
            <h1 className="relative text-7xl sm:text-9xl md:text-[10rem] font-black italic tracking-tighter uppercase select-none leading-none text-white drop-shadow-[0_10px_10px_rgba(0,0,0,0.5)]">
              HPM <span className="text-hpmBlue drop-shadow-[0_0_40px_rgba(0,136,204,0.7)]">RACING</span>
            </h1>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="flex flex-wrap justify-center items-center gap-x-8 gap-y-4 text-[10px] font-black uppercase tracking-[0.6em] text-muted-foreground/50 italic"
          >
            <span className="flex items-center gap-2 transition-colors hover:text-hpmBlue cursor-default"><ShieldCheck className="w-3.5 h-3.5" /> INTEGRITY</span>
            <span className="flex items-center gap-2 transition-colors hover:text-hpmBlue cursor-default"><Gauge className="w-3.5 h-3.5" /> PRECISION</span>
            <span className="flex items-center gap-2 transition-colors hover:text-hpmBlue cursor-default"><Zap className="w-3.5 h-3.5" /> VELOCITY</span>
          </motion.div>
        </motion.div>
        {/* Action Grid */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8, ease: "easeOut" }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-5xl"
        >
          <HeroButton
            title="Setup Terminal"
            subtitle="Diagnostic Uplink"
            description="Real-time handling diagnostics and corrective engineering matrix for competitive race sessions."
            onClick={() => navigate('/adjustments')}
            color="blue"
          />
          <HeroButton
            title="Nascar Packages"
            subtitle="Elite Builds"
            description="Access our encrypted database of professional setup builds optimized for high-performance track profiles."
            onClick={() => navigate('/setups')}
            color="orange"
          />
        </motion.div>
        {/* Brand Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="mt-24 flex flex-col items-center gap-4"
        >
          <div className="flex items-center gap-4 text-[9px] font-black uppercase tracking-[0.4em] text-muted-foreground/30 italic">
            <span>PROD_BRANCH_2025</span>
            <div className="w-1 h-1 rounded-full bg-hpmBlue opacity-30" />
            <span>BUILD: 4.2.0-STABLE</span>
          </div>
          <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-muted-foreground/20 italic">
            Developed for Virtual Motorsports Excellence // © HPM Racing
          </p>
        </motion.div>
      </div>
    </div>
  );
};
const HeroButton: React.FC<{
  title: string;
  subtitle: string;
  description: string;
  onClick: () => void;
  color: 'blue' | 'orange';
}> = ({ title, subtitle, description, onClick, color }) => {
  const isBlue = color === 'blue';
  return (
    <motion.button
      whileHover={{ scale: 1.02, y: -6 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={cn(
        "relative p-10 text-left rounded-3xl border-2 transition-all duration-500 overflow-hidden group bg-hpmDark/60 backdrop-blur-xl",
        isBlue
          ? "border-hpmBlue/20 hover:border-hpmBlue hover:shadow-[0_0_50px_rgba(0,136,204,0.25)]"
          : "border-orange-500/20 hover:border-orange-500 hover:shadow-[0_0_50px_rgba(249,115,22,0.25)]"
      )}
    >
      {/* Hover Scanning Line Effect */}
      <motion.div 
        className={cn(
          "absolute top-0 left-0 right-0 h-[2px] opacity-0 group-hover:opacity-100 z-20",
          isBlue ? "bg-hpmBlue shadow-[0_0_15px_#0088CC]" : "bg-orange-500 shadow-[0_0_15px_#F97316]"
        )}
        animate={{ top: ['-5%', '105%'] }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
      />
      {/* Internal Glow Gradient */}
      <div className={cn(
        "absolute -top-1/4 -right-1/4 w-1/2 h-1/2 blur-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-1000",
        isBlue ? "bg-hpmBlue" : "bg-orange-500"
      )} />
      <div className="relative z-10 flex flex-col h-full">
        <div className="flex items-center justify-between mb-3">
          <span className={cn(
            "text-[10px] font-black uppercase tracking-[0.4em] italic",
            isBlue ? "text-hpmBlue" : "text-orange-400"
          )}>
            {subtitle}
          </span>
          <Terminal className={cn("w-4 h-4 opacity-20", isBlue ? "text-hpmBlue" : "text-orange-400")} />
        </div>
        <h3 className="text-4xl font-black italic uppercase tracking-tighter mb-6 text-white group-hover:translate-x-2 transition-transform duration-500">
          {title}
        </h3>
        <p className="text-sm text-muted-foreground/50 font-medium leading-relaxed mb-10 flex-grow pr-8">
          {description}
        </p>
        <div className={cn(
          "flex items-center gap-3 text-[11px] font-black uppercase tracking-[0.3em] transition-colors",
          isBlue ? "text-hpmBlue/80 group-hover:text-hpmBlue" : "text-orange-400/80 group-hover:text-orange-400"
        )}>
          ACCESS PROTOCOL <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </motion.button>
  );
};
export default LandingPage;